import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import type { CanonicalFile, FileVersion } from "@/lib/data/schemas/files";
import {
  restoreFileVersion,
  fileOperationMessage,
} from "@/lib/data/requests/files/canonical-files";
import { invalidateFileQueries } from "./canonical-files";

const pendingRestores = new Map<
  string,
  { versionId: string; revision: number; requestId: string }
>();

export function useRestoreFileVersion(
  fileId: string,
  file: CanonicalFile | undefined,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (version: FileVersion) => {
      if (!file)
        throw new Error("Refresh this file before restoring a version.");
      if (file.scope === "template" || !file.capabilities.canRestoreVersion)
        throw new Error("Restoring a version is unavailable for this file.");
      let operation = pendingRestores.get(fileId);
      if (!operation || operation.versionId !== version.id) {
        operation = {
          versionId: version.id,
          revision: file.revision,
          requestId: crypto.randomUUID(),
        };
        pendingRestores.set(fileId, operation);
      }
      return restoreFileVersion(fileId, version.id, {
        revision: operation.revision,
        requestId: operation.requestId,
      });
    },
    retry: false,
    onSuccess: async () => {
      pendingRestores.delete(fileId);
      toast.success("File restored successfully");
      await invalidateFileQueries(client);
    },
    onError: async (error) => {
      toast.error(
        fileOperationMessage(error, "Unable to make this version current."),
      );
      if (
        isAxiosError(error) &&
        error.response &&
        error.response.status < 500
      ) {
        pendingRestores.delete(fileId);
        await invalidateFileQueries(client);
      }
    },
  });
}
